<template>
  <div class="drawer-filter procurement-filter-drawer">
    <a class="filter-trigger" :title="$t('shippingPlan.filterTitle')" @click="toggle">
      <i class="fa fa-filter fa-2x"></i>
      <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
    </a>
    <div v-if="isOpen" class="filter-overlay" @click="isOpen = false"></div>
    <div class="filter-panel" :class="{ open: isOpen }">
      <div class="filter-panel-header">
        <span>{{ $t("shippingPlan.filterTitle") }}<template v-if="activeFilterCount > 0"> ({{ activeFilterCount }})</template></span>
        <md-button class="md-simple md-just-icon md-round" @click="isOpen = false"><md-icon>clear</md-icon></md-button>
      </div>
      <div class="filter-panel-body">
        <div class="filter-section">
          <div class="filter-section-title">{{ $t("procurement.filters.supplier") }}</div>
          <div class="filter-select">
            <v-select
              v-model="localSupplier"
              class="custom-select"
              label="name"
              :options="supplierOptions"
              :filter="filterSupplier"
              :placeholder="$t('procurement.filters.supplier')"
              append-to-body
              :calculate-position="alignDropdown"
            />
          </div>
        </div>

        <!-- Kennzahlfilter mit Operator wie bei Angeboten und Produkten (Trello evD8Gwb4 Punkt 19) -->
        <div class="filter-section">
          <div class="filter-section-title">{{ $t("procurement.filters.presets") }}</div>
          <div class="filter-presets">
            <md-button v-for="p in presets" :key="p.key" class="md-simple md-sm" :class="{ 'filter-preset--active': presetActive(p) }" @click="togglePreset(p)">{{ p.label }}</md-button>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-section-title">{{ $t("procurement.filters.figures") }}</div>
          <div v-for="k in kpiFields" :key="k.key" class="filter-kpi">
            <span class="filter-kpi-label">{{ k.label }}</span>
            <select v-model="localKpi[k.key].op" class="filter-kpi-op">
              <option value="">{{ $t("products.filter.opNone") }}</option>
              <option value="gt">&gt;</option>
              <option value="lt">&lt;</option>
              <option value="eq">=</option>
            </select>
            <input
              v-model="localKpi[k.key].value"
              type="number"
              class="filter-kpi-value"
              :step="1 / Math.pow(10, k.decimals)"
              :disabled="!localKpi[k.key].op"
              :class="{ 'filter-kpi-value--invalid': !kpiValid(k) }"
            />
            <span class="filter-kpi-unit">{{ $t("products.filter.unit." + k.unit) }}</span>
          </div>
        </div>
      </div>
      <div class="filter-panel-footer">
        <md-button class="md-secondary" @click="clearFilters">{{ $t("shippingPlan.clear") }}</md-button>
        <md-button class="md-success" :disabled="!allKpiValid" @click="applyFilters">{{ $t("shippingPlan.apply") }}</md-button>
      </div>
    </div>
  </div>
</template>

<script>
import { alignDropdown } from "@/mixins/dropdownPosition";
import { DrawerScrollLockMixin } from "@/mixins/drawerScrollLock";

const copyKpi = (kpi) => Object.fromEntries(Object.entries(kpi).map(([key, e]) => [key, { ...e }]));

// Filter-Schublade des Bestellwesens nach dem Muster des Lieferplans (Kundenwunsch 03.11.).
// Der Zustand bleibt im Bestellwesen; hier wird auf einer Kopie gearbeitet und erst mit "Anwenden" uebernommen.
export default {
  name: "ProcurementFilterDrawer",
  mixins: [DrawerScrollLockMixin],
  props: {
    supplier: { type: Object, default: null },
    kpi: { type: Object, required: true },
    kpiFields: { type: Array, required: true },
    presets: { type: Array, default: () => [] },
    supplierOptions: { type: Array, default: () => [] },
    filterSupplier: { type: Function, required: true },
  },
  data() {
    return {
      isOpen: false,
      localSupplier: this.supplier,
      localKpi: copyKpi(this.kpi),
    };
  },
  computed: {
    activeKpiCount() {
      return Object.values(this.kpi).filter((e) => e.op && e.value !== "" && e.value != null).length;
    },
    activeFilterCount() {
      return (this.supplier ? 1 : 0) + this.activeKpiCount;
    },
    allKpiValid() {
      return this.kpiFields.every((k) => this.kpiValid(k));
    },
  },
  mounted() {
    document.addEventListener("click", this.onClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onClickOutside);
  },
  methods: {
    alignDropdown,
    toggle() {
      if (!this.isOpen) {
        this.localSupplier = this.supplier;
        this.localKpi = copyKpi(this.kpi);
      }
      this.isOpen = !this.isOpen;
    },
    kpiValid(k) {
      const entry = this.localKpi[k.key];
      if (!entry.op || entry.value === "" || entry.value == null) return true;
      const text = String(entry.value);
      if (!Number.isFinite(Number(text))) return false;
      const decimals = text.includes(".") ? text.split(".")[1].length : 0;
      return decimals <= k.decimals;
    },
    presetActive(p) {
      const e = this.localKpi[p.key];
      return e.op === p.op && e.value !== "" && Number(e.value) === p.value;
    },
    togglePreset(p) {
      this.localKpi[p.key] = this.presetActive(p) ? { op: "", value: "" } : { op: p.op, value: String(p.value) };
    },
    clearFilters() {
      this.localSupplier = null;
      Object.keys(this.localKpi).forEach((key) => {
        this.localKpi[key] = { op: "", value: "" };
      });
    },
    applyFilters() {
      this.$emit("apply-filters", { supplier: this.localSupplier, kpi: copyKpi(this.localKpi) });
      this.isOpen = false;
    },
    onClickOutside(e) {
      // Auswahllisten (v-select mit append-to-body) liegen ausserhalb des Panels
      const inMenu = e.target.closest && e.target.closest(".md-menu-content, .vs__dropdown-menu");
      if (this.isOpen && !inMenu && document.body.contains(e.target) && !this.$el.contains(e.target)) {
        this.isOpen = false;
      }
    },
  },
};
</script>

<style>
.procurement-filter-drawer .filter-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid #bbb;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  position: relative;
}
.procurement-filter-drawer .filter-trigger i.fa {
  font-size: 20px;
  color: #fff !important;
}
.procurement-filter-drawer .filter-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #f44336;
  color: #fff;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
}
.procurement-filter-drawer .filter-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1032;
}
.procurement-filter-drawer .filter-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 460px;
  max-width: 95vw;
  background: #fff;
  z-index: 1033;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 18px rgba(0, 0, 0, 0.25);
  transform: translateX(100%);
  transition: transform 0.25s ease;
  visibility: hidden;
}
.procurement-filter-drawer .filter-panel.open {
  transform: translateX(0);
  visibility: visible;
}
.procurement-filter-drawer .filter-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px 10px 20px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 16px;
  font-weight: 500;
}
.procurement-filter-drawer .filter-panel-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 20px 16px 20px;
  text-align: left;
}
.procurement-filter-drawer .filter-panel-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 20px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}
.procurement-filter-drawer .filter-section {
  margin-top: 14px;
}
.procurement-filter-drawer .filter-section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9e9e9e;
  border-bottom: 1px solid #eee;
  padding-bottom: 4px;
  margin-bottom: 4px;
}
.procurement-filter-drawer .filter-select {
  margin: 6px 0 4px 0;
}
.procurement-filter-drawer .filter-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.procurement-filter-drawer .filter-presets .md-button {
  margin: 0;
  text-transform: none;
}
.procurement-filter-drawer .filter-presets .md-button.filter-preset--active {
  color: #7f915a !important;
  font-weight: 600;
}
.procurement-filter-drawer .filter-kpi {
  display: grid;
  grid-template-columns: 1fr 64px 96px 52px;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  font-size: 13px;
}
.procurement-filter-drawer .filter-kpi-op,
.procurement-filter-drawer .filter-kpi-value {
  padding: 3px 4px;
  border: 1px solid #ddd;
  border-radius: 3px;
  font-size: 13px;
}
.procurement-filter-drawer .filter-kpi-value {
  text-align: right;
}
.procurement-filter-drawer .filter-kpi-value:disabled {
  background: #f6f6f6;
}
.procurement-filter-drawer .filter-kpi-value--invalid {
  border-color: #f44336;
}
.procurement-filter-drawer .filter-kpi-unit {
  color: #777;
  font-size: 12px;
}
</style>
